import { useMutation, useQuery, useQueryClient } from "react-query";

import { SubscriptionDto } from "doxecure-commons/doxecureClientTypes";
import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { buyCreditsPack, cancelDowngradingRenewalToStandardSubscription, cancelTerminationSubscriptionRequest, getActiveSubscription, requestDowngradeRenewalToStandardSubscription, requestTerminatingSubscription, startSubscriptionNow, upgradeSubscriptionToUnlimited } from "@services/corporations.service";

import { OrdersID, SubscriptionID } from "./hooksConstants";
import { useAuthContext } from "./useAuthContext";

export const useSubscription = (isPendingPayment: boolean) => {
    const { connectedUser, isAuthenticated } = useAuthContext();
    const queryClient = useQueryClient();

    const corporationApid = connectedUser?.corporations?.first()?.apid;

    const query = useQuery<Nullable<SubscriptionDto>>({
        queryKey: [SubscriptionID, corporationApid],
        queryFn: () => getActiveSubscription(corporationApid),
        enabled: isAuthenticated && $ok(corporationApid),
        staleTime: 5 * 60 * 1000,
        refetchInterval: isPendingPayment ? 2000 : false,
    });

    const invalidate = () => {
        queryClient.invalidateQueries([SubscriptionID, corporationApid]);
        queryClient.invalidateQueries([OrdersID, corporationApid]);
    };

    const buyExtraPack = useMutation({
        mutationFn: () => buyCreditsPack(corporationApid),
        onSuccess: invalidate
    });

    const cancelDowngradingRequest = useMutation({
        mutationFn: () => cancelDowngradingRenewalToStandardSubscription(corporationApid),
        onSuccess: invalidate
    });

    const cancelTerminationRequest = useMutation({
        mutationFn: () => cancelTerminationSubscriptionRequest(corporationApid),
        onSuccess: invalidate
    });

    const requestCancelSubscription = useMutation({
        mutationFn: () => requestTerminatingSubscription(corporationApid),
        onSuccess: invalidate,
    });

    const requestDowngradRenewalToStandardPlan = useMutation({
        mutationFn: () => requestDowngradeRenewalToStandardSubscription(corporationApid),
        onSuccess: invalidate,
    });

    const startSubscriptionImmediately = useMutation({
        mutationFn: (unlimited: boolean) => startSubscriptionNow(corporationApid, unlimited),
        onSuccess: invalidate,
    });

    const upgradeToUnlimited = useMutation({
        mutationFn: () => upgradeSubscriptionToUnlimited(corporationApid),
        onSuccess: invalidate,
    });

    return {
        ...query,
        buyExtraPack,
        cancelDowngradingRequest,
        cancelTerminationRequest,
        requestCancelSubscription,
        requestDowngradRenewalToStandardPlan,
        startSubscriptionImmediately,
        upgradeToUnlimited
    }
}